import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ConsultationContextType {
  isPopupOpen: boolean;
  openPopup: (source?: 'header' | 'calculator' | 'ebook' | 'promotion', prizeWon?: string) => void;
  closePopup: () => void;
  popupSource: 'header' | 'calculator' | 'ebook' | 'promotion';
  prizeWon?: string;
  handleConsultationSuccess: () => void;
}

const ConsultationContext = createContext<ConsultationContextType | undefined>(undefined);

const CONSULTATION_STORAGE_KEY = 'hasConsultedBefore';

export const ConsultationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupSource, setPopupSource] = useState<'header' | 'calculator' | 'ebook' | 'promotion'>('header');
  const [prizeWon, setPrizeWon] = useState<string | undefined>();

  const openPopup = (source: 'header' | 'calculator' | 'ebook' | 'promotion' = 'header', prize?: string) => {
    setPopupSource(source);
    setPrizeWon(prize);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const handleConsultationSuccess = () => {
    localStorage.setItem(CONSULTATION_STORAGE_KEY, 'true');
    // Trigger re-render for components that check hasConsultedBefore
    window.dispatchEvent(new Event('consultationCompleted'));

    // If this was triggered from ebook page, unlock the ebook
    if (popupSource === 'ebook') {
      const pendingEbookId = sessionStorage.getItem('pendingEbookId');
      if (pendingEbookId) {
        const event = new CustomEvent('ebookUnlocked', { detail: { ebookId: pendingEbookId } });
        window.dispatchEvent(event);
        sessionStorage.removeItem('pendingEbookId');
      }
    }
  };

  return (
    <ConsultationContext.Provider
      value={{
        isPopupOpen,
        openPopup,
        closePopup,
        popupSource,
        prizeWon,
        handleConsultationSuccess,
      }}
    >
      {children}
    </ConsultationContext.Provider>
  );
};

export const useConsultation = () => {
  const context = useContext(ConsultationContext);
  if (context === undefined) {
    throw new Error('useConsultation must be used within a ConsultationProvider');
  }
  return context;
};
